import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, FileText, AlertTriangle, CreditCard, ScrollText, BarChart3, Gauge } from "lucide-react";
import AdminComplimentary from "@/pages/admin/AdminComplimentary";
import AdminAccounts from "@/pages/admin/AdminAccounts";
import AdminEmail from "@/pages/admin/AdminEmail";
import AdminCrossPromo from "@/pages/admin/AdminCrossPromo";

function Stat({ label, value, icon: Icon, onActivate, destinationLabel }) {
  if (!onActivate) {
    return (
      <div className="card-donkey rounded-2xl p-5">
        <div className="flex items-center gap-2 text-donkey-grey mb-2"><Icon className="w-4 h-4 text-donkey-orange" /><span className="text-xs uppercase tracking-wider">{label}</span></div>
        <p className="font-mono text-2xl text-donkey-warm">{value}</p>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onActivate}
      aria-label={`${label}: ${value}. ${destinationLabel}`}
      data-testid={`admin-stat-${label.toLowerCase()}`}
      className="card-donkey rounded-2xl p-5 text-left w-full cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:border-donkey-orange/60 hover:shadow-[0_8px_28px_rgba(249,115,22,0.15)] focus:outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange focus-visible:ring-offset-2 focus-visible:ring-offset-donkey-bg group"
    >
      <div className="flex items-center gap-2 text-donkey-grey mb-2"><Icon className="w-4 h-4 text-donkey-orange" /><span className="text-xs uppercase tracking-wider">{label}</span></div>
      <p className="font-mono text-2xl text-donkey-warm">{value}</p>
      <span className="mt-1 block text-[11px] text-donkey-grey group-hover:text-donkey-orange transition-colors">{destinationLabel} →</span>
    </button>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [failed, setFailed] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [audit, setAudit] = useState([]);
  const [adminLog, setAdminLog] = useState([]);
  const [q, setQ] = useState("");
  const [ents, setEnts] = useState([]);
  const [planIds, setPlanIds] = useState([]);
  const [entEmail, setEntEmail] = useState("");
  const [entPlan, setEntPlan] = useState("prove");
  const [entStatus, setEntStatus] = useState("active");
  const [entInterval, setEntInterval] = useState("month");
  const [tab, setTab] = useState("users");
  const tabsRef = useRef(null);

  const goTab = (v) => {
    setTab(v);
    setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };

  const loadEnts = async (search = "") => {
    const { data } = await api.get("/admin/entitlements", { params: { q: search } });
    setEnts(data.entitlements); setPlanIds(data.plan_ids);
  };
  const setEntitlement = async () => {
    try {
      await api.post("/admin/entitlement", { email: entEmail, plan: entPlan, status: entStatus, interval: entInterval });
      toast.success("Entitlement updated.");
      loadEnts(entEmail);
    } catch (e) { toast.error("Could not update entitlement."); }
  };
  const grantVerdict = async () => {
    try {
      await api.post("/admin/grant-verdict", { email: entEmail });
      toast.success("Verdict credit granted.");
      loadEnts(entEmail);
    } catch (e) { toast.error("Could not grant credit."); }
  };

  const loadUsers = async (search = "") => { const { data } = await api.get("/admin/users", { params: { q: search } }); setUsers(data.users); };
  useEffect(() => {
    (async () => {
      const [s, r, f, p, a] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/reports"), api.get("/admin/failed"),
        api.get("/admin/purchases"), api.get("/admin/audit"),
      ]);
      setStats(s.data); setReports(r.data.reports); setFailed(f.data.failed);
      setPurchases(p.data.purchases); setAudit(a.data.logs);
      loadUsers();
      loadEnts();
      api.get("/admin/audit-log").then(({ data }) => setAdminLog(data.logs)).catch(() => {});
    })();
  }, []);

  const suspend = async (uid, current) => {
    await api.post("/admin/users/suspend", { user_id: uid, suspended: !current });
    toast.success(current ? "Account reinstated." : "Account suspended.");
    loadUsers(q);
  };
  const regenerate = async (rid) => {
    await api.post(`/reports/${rid}/retry`);
    toast.success("Regeneration queued.");
  };

  return (
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
      <h1 className="font-heading font-bold text-3xl tracking-tight text-donkey-warm mb-2">Admin</h1>
      <p className="text-donkey-grey mb-8 text-sm">No passwords, no card details — just what you need to run the stable.</p>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          <Stat label="Users" value={stats.users} icon={Users} onActivate={() => goTab("users")} destinationLabel="View all users" />
          <Stat label="Interviews" value={stats.interviews} icon={FileText} onActivate={() => goTab("users")} destinationLabel="View users" />
          <Stat label="Reports" value={stats.reports} icon={ScrollText} onActivate={() => goTab("reports")} destinationLabel="View reports" />
          <Stat label="Failed" value={stats.failed_generations} icon={AlertTriangle} onActivate={() => goTab("failed")} destinationLabel="View failed" />
          <Stat label="Purchases" value={stats.purchases} icon={CreditCard} onActivate={() => goTab("purchases")} destinationLabel="View purchases" />
          <Stat label="Revenue" value={`£${stats.revenue}`} icon={BarChart3} onActivate={() => goTab("purchases")} destinationLabel="View purchases" />
        </div>
      )}

      <div ref={tabsRef}>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-donkey-deep border border-white/10 flex flex-wrap h-auto p-1 gap-1">
          {[["users", "Users"], ["complimentary", "Complimentary"], ["email", "Email"], ["plans", "Plans"], ["reports", "Reports"], ["failed", "Failed"], ["purchases", "Purchases"], ["crosspromo", "Cross-promo"], ["audit", "Audit"], ["adminlog", "Admin Log"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="text-donkey-grey data-[state=active]:bg-donkey-orange data-[state=active]:text-donkey-black" data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <AdminAccounts />
        </TabsContent>

        <TabsContent value="complimentary" className="mt-6">
          <AdminComplimentary />
        </TabsContent>

        <TabsContent value="email" className="mt-6">
          <AdminEmail />
        </TabsContent>

        <TabsContent value="plans" className="mt-6">
          <div className="card-donkey rounded-2xl p-5 mb-5">
            <div className="flex items-center gap-2 mb-4"><Gauge className="w-4 h-4 text-donkey-orange" /><h3 className="font-heading font-semibold text-donkey-warm text-sm">Configure a user's plan (audited)</h3></div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <Input value={entEmail} onChange={(e) => setEntEmail(e.target.value)} placeholder="user email" className="bg-donkey-deep border-white/15 text-donkey-warm" data-testid="admin-ent-email" />
              <select value={entPlan} onChange={(e) => setEntPlan(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10" data-testid="admin-ent-plan">
                {planIds.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={entInterval} onChange={(e) => setEntInterval(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10">
                <option value="month">monthly</option><option value="year">annual</option>
              </select>
              <select value={entStatus} onChange={(e) => setEntStatus(e.target.value)} className="bg-donkey-deep border border-white/15 rounded-md text-donkey-warm text-sm px-3 h-10">
                {["active", "cancelled", "past_due", "none"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button onClick={setEntitlement} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="admin-set-ent-btn">Set plan</Button>
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={grantVerdict} className="border-white/15 bg-transparent text-donkey-warm hover:bg-white/5" data-testid="admin-grant-verdict-btn">Grant a one-off verdict credit</Button>
              <Button size="sm" variant="outline" onClick={() => loadEnts(entEmail)} className="border-white/15 bg-transparent text-donkey-warm hover:bg-white/5">Search</Button>
            </div>
          </div>
          <div className="card-donkey rounded-2xl overflow-hidden">
            {ents.map((e) => (
              <div key={e.user_id} className="flex items-center justify-between px-5 py-3 border-b border-white/5 last:border-0 text-sm gap-3">
                <span className="text-donkey-warm truncate flex-1">{e.email}</span>
                <span className="text-donkey-orange capitalize">{e.plan}</span>
                <span className="text-donkey-grey text-xs">{e.status}</span>
                <span className="text-donkey-grey text-xs">credits: {e.verdict_credits_available}</span>
              </div>
            ))}
            {ents.length === 0 && <p className="p-5 text-sm text-donkey-grey">No entitlement records yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="reports" className="mt-6">
          <div className="card-donkey rounded-2xl overflow-hidden">
            {reports.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-5 py-3 border-b border-white/5 last:border-0 text-sm">
                <span className="font-mono text-xs text-donkey-grey">{r.id.slice(0, 8)}</span>
                <span className={r.gen_status === "complete" ? "text-donkey-green" : r.gen_status === "failed" ? "text-donkey-red" : "text-donkey-grey"}>{r.gen_status}</span>
                <span className="text-donkey-grey">{r.unlocked ? "unlocked" : "locked"}</span>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="failed" className="mt-6">
          <div className="card-donkey rounded-2xl overflow-hidden">
            {failed.length === 0 && <p className="p-5 text-sm text-donkey-grey">No failed generations. The donkey is behaving.</p>}
            {failed.map((r) => (
              <div key={r.id} className="flex items-center justify-between px-5 py-3 border-b border-white/5 last:border-0 text-sm gap-4">
                <span className="font-mono text-xs text-donkey-grey shrink-0">{r.id.slice(0, 8)}</span>
                <span className="text-donkey-red text-xs truncate flex-1">{r.error}</span>
                <Button size="sm" onClick={() => regenerate(r.id)} className="bg-donkey-orange text-donkey-black font-semibold shrink-0" data-testid={`regen-${r.id}`}>Regenerate</Button>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="purchases" className="mt-6">
          <div className="card-donkey rounded-2xl overflow-hidden">
            {purchases.map((p, i) => (
              <div key={i} className="flex items-center justify-between px-5 py-3 border-b border-white/5 last:border-0 text-sm">
                <span className="capitalize text-donkey-warm/80">{(p.plan || "").replace(/_/g, " ")}</span>
                <span className="font-mono text-donkey-grey">£{Number(p.amount).toFixed(2)}</span>
                <span className={p.payment_status === "paid" ? "text-donkey-green" : "text-donkey-grey"}>{p.payment_status}</span>
              </div>
            ))}
            {purchases.length === 0 && <p className="p-5 text-sm text-donkey-grey">No purchases yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="crosspromo" className="mt-6">
          <AdminCrossPromo />
        </TabsContent>

        <TabsContent value="audit" className="mt-6">
          <div className="card-donkey rounded-2xl overflow-hidden">
            {audit.map((l, i) => (
              <div key={i} className="px-5 py-3 border-b border-white/5 last:border-0 text-sm text-donkey-grey">
                <span className="text-donkey-warm">{l.actor}</span> · {l.action} · {l.target}
              </div>
            ))}
            {audit.length === 0 && <p className="p-5 text-sm text-donkey-grey">No audit events yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="adminlog" className="mt-6">
          <div className="card-donkey rounded-2xl overflow-hidden" data-testid="admin-log">
            {adminLog.map((l, i) => (
              <div key={i} className="px-5 py-3 border-b border-white/5 last:border-0 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-donkey-warm">{l.actor_email}</span>
                  <span className="text-donkey-orange text-xs">{l.action}</span>
                  {l.target_email && <span className="text-donkey-grey text-xs">→ {l.target_email}</span>}
                  <span className="text-donkey-grey text-xs ml-auto">{l.at?.slice(0, 19).replace("T", " ")}</span>
                </div>
                {l.reason && <p className="text-xs text-donkey-grey mt-1">reason: {l.reason}</p>}
              </div>
            ))}
            {adminLog.length === 0 && <p className="p-5 text-sm text-donkey-grey">No admin actions logged yet.</p>}
          </div>
        </TabsContent>
      </Tabs>
      </div>
    </div>
  );
}
